import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, OnDestroy } from '@angular/core';
import { RouterModule } from '@angular/router';
import {
  ChildrenSelectorComponent,
  Student,
} from '../../shared/children-selector/children-selector.component';
import { TranslatePipe } from '@ngx-translate/core';
import { HttpClient } from '@angular/common/http';
import { PageTitleComponent } from '../../shared/page-title/page-title.component';
import { LoaderComponent } from '../../shared/loader/loader.component';
import { forkJoin, Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';
import { DownloadFilesService } from '../../services/download-files.service';
import { NotificationService } from '../../services/notification.service';
import { environment } from '../../../environments/environment';

// Définition d’un fichier de devoir
interface HomeworkFile {
  id: number;
  title: string;
  fileName: string;
  path: string;
  description?: string;
}

interface CahierFile {
  url: string;
  id: number;
  name: string;
  path: string;
}

// Définition de CahierItem
interface CahierItem {
  id?: number;
  matiere: string;
  creationDate: string;
  color: string;
  vu: string;
  atomaticDate: string;
  nbrExercices: number;
  name: string | null;
  totalPage: number;
  isNew: boolean;
  isExpanded?: boolean;
  detailTitle?: string;
  detailCorrectionDate?: string;
  detailDescription?: string;
  detailFiles?: CahierFile[];
  detailsLoaded?: boolean;
  isDetailsLoading?: boolean;
  detailTeacherName?: string;
  typeLabel?: string;
  displayDate?: string;
  backgroundClass?: string;
}

@Component({
  selector: 'app-cahier-de-text',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    ChildrenSelectorComponent,
    TranslatePipe,
    PageTitleComponent,
    LoaderComponent,
  ],
  templateUrl: './cahier-de-text.component.html',
  styleUrls: ['./cahier-de-text.component.css'],
})
export class CahierDeTextComponent implements OnDestroy {
  private readonly apiBaseUrl = environment.apiUrl.replace(/\/$/, '');
  private destroy$ = new Subject<void>();
  currentPage = 1;
  itemsPerPage = 12;

  students: Student[] = [];
  selectedStudent: Student | null = null;
  cahierList: CahierItem[] = [];
  totalItems = 0;
  totalPagesFromApi: number | undefined;
  isLoading = false;
  userData: any;

  constructor(
    private http: HttpClient, 
    private cdr: ChangeDetectorRef,
    private downloadService: DownloadFilesService,
    private notificationService: NotificationService
  ) {}

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private buildAbsoluteUrl(p: string): string {
    try {
      const raw = (p || '').trim();
      if (!raw) return '';
      if (/^https?:\/\//i.test(raw)) return encodeURI(raw);

      let processed = raw;

      const webappsIndex = raw.indexOf('/webapps/');
      if (webappsIndex !== -1) {
        processed = raw.substring(webappsIndex + '/webapps'.length);
      }

      const base = 'https://plateforme.educanet.pro/';
      const normalized = processed.replace(/^\/+/, '');
      return encodeURI(new URL(normalized, base).toString());
    } catch {
      return p;
    }
  }

  private formatDateForDisplay(dateStr: string | undefined | null): string {
    if (!dateStr) return '';
    const raw = dateStr.split('T')[0].split(' ')[0];
    const parts = raw.split('-');
    if (parts.length === 3) {
      const [year, month, day] = parts;
      if (year && month && day) {
        return `${day}-${month}-${year}`;
      }
    }
    return raw;
  }

  getItemColor(item: CahierItem): string {
    const displayDate = (item.displayDate || '').toLowerCase();
    const creationDate = (item.creationDate || '').toLowerCase();
    const typeLabel = (item.typeLabel || '').toLowerCase();

    // Priority 1: Déjà Corrigé -> Grey
    if (displayDate.includes('déjà corrigé') || displayDate.includes('deja corrige') ||
        creationDate.includes('déjà corrigé') || creationDate.includes('deja corrige')) {
      return '#9E9E9E'; // Grey
    }

    // Priority 2: Type Label checks
    if (typeLabel.includes('exercice spécifique')) {
      return '#FFC107'; // Yellow
    } else if (typeLabel.includes('cours') || typeLabel.includes('عمل منجز')) {
      return '#4CAF50'; // Green
    } else if (typeLabel.includes('exercice à faire') || typeLabel.includes('à faire')) {
      return '#F44336'; // Red
    }

    return '#009688'; // Default teal
  }

  onStudentSelected(student: Student) {
    this.selectedStudent = student;
    this.currentPage = 1; // Reset to first page when selecting a new student
    this.totalItems = 0; // Reset total items
    this.loadCahier(student, this.currentPage);
  }

  onStudentsLoaded(students: Student[]) {
    this.students = students;
    if (!this.selectedStudent && students.length > 0) {
      this.selectedStudent = students[0];
      this.loadCahier(students[0], this.currentPage);
    }
  }
loadCahier(student: Student, pageNumber: number) {
  setTimeout(() => (this.isLoading = true));

  const storedData = localStorage.getItem('userData');
  if (!storedData) {
    console.error('❌ No user data found in localStorage');
    setTimeout(() => (this.isLoading = false));
    return;
  }

  this.userData = JSON.parse(storedData);

  // ✅ Correction de l’ordre des paramètres (pageNumber, itemsPerPage)
  const listUrl = `${this.apiBaseUrl}/homeWork/listByStudent/${student.idStudent}/${pageNumber}/${this.itemsPerPage}`;

  this.http.get<any>(listUrl).subscribe({
    next: (res) => {
      const list = Array.isArray(res?.response) ? res.response : [];

      this.totalItems = list.length;
      
      // Extraction du nombre total de pages depuis la réponse API
      this.totalPagesFromApi = undefined;
      
      // 1. Vérifier à la racine de la réponse
       if (res?.totalPage !== undefined) this.totalPagesFromApi = Math.ceil(res.totalPage / this.itemsPerPage);
       else if (res?.totalpage !== undefined) this.totalPagesFromApi = Math.ceil(res.totalpage / this.itemsPerPage);
       
       // 2. Vérifier dans le premier élément de la liste (pattern courant dans ce projet)
       else if (list.length > 0) {
         if (list[0].totalPage !== undefined) {
           // FIX: L'API renvoie le nombre total d'éléments dans 'totalPage', pas le nombre de pages.
           this.totalPagesFromApi = Math.ceil(list[0].totalPage / this.itemsPerPage);
         } else if (list[0].totalpage !== undefined) {
           this.totalPagesFromApi = Math.ceil(list[0].totalpage / this.itemsPerPage);
         } else if (list[0].nbrHomeWork !== undefined) {
           this.totalPagesFromApi = Math.ceil(list[0].nbrHomeWork / this.itemsPerPage);
         } else if (list[0].totalElements !== undefined) {
             this.totalPagesFromApi = Math.ceil(list[0].totalElements / this.itemsPerPage);
         }
       }

      if (!list.length) {
        this.cahierList = [];
        setTimeout(() => (this.isLoading = false));
        return;
      }

      // ✅ Pas besoin d’appeler getDetailsHomeWork — les détails sont déjà inclus dans la réponse
      const newList: CahierItem[] = list.map((item: any) => {
        // Adaptation pour la nouvelle structure API (plate) vs ancienne (imbriquée dans homeWork)
        const isNewStructure = 'matiere' in item;
        
        let id = item.id;
        let matiere = '—';
        let enseignant = '—';
        let titre = 'Sans titre';
        let dateCorrection = '';
        let description = '';
        let isNew = false;
        let files: CahierFile[] = [];
        let vu: number | string = 0;

        if (isNewStructure) {
            // Nouvelle structure (d'après Swagger)
            id = item.id;
            matiere = item.matiere || '—';
            // Enseignant absent du Swagger simplifié, on garde '-' ou on cherche si présent
            enseignant = item.teacherName || '—'; 
            titre = item.name || 'Sans titre';
            // "atomaticDate" est une typo potentielle vue dans le Swagger
            dateCorrection = (item.atomaticDate || item.creationDate || '').split(' ')[0]; // Display only the date part
            description = item.description || '';
            
            // Gestion du statut "vu"
            vu = item.vu; // "string" dans Swagger
            isNew = item.vu !== '1' && item.vu !== 1;

            // Gestion des fichiers (si présents à la racine)
            const rawFiles = item.files || item.homeworkfiles || [];
            if (Array.isArray(rawFiles)) {
                files = rawFiles.map((f: any) => {
                    const url = this.buildAbsoluteUrl(f.path || f.fileName);
                    return {
                        id: f.id,
                        name: f.title || f.fileName || 'Fichier',
                        path: url,
                        url: url,
                    };
                });
            }
        } else {
            // Ancienne structure (fallback)
            const hw = item.homeWork || {};
            const teacherUser = hw.teacher?.user || {};
            const rawFiles = hw.homeworkfiles?.fileEducanet || [];

            id = hw.id;
            matiere = hw.courseName || '—';
            enseignant = `${teacherUser.firstName || ''} ${teacherUser.lastName || ''}`.trim() || '—';
            titre = hw.name || hw.subname || 'Sans titre';
            dateCorrection = hw.atomicDate || hw.creationDate || '';
            isNew = item.homeworkViewed === false;
            description = hw.description || '';
            vu = item.homeworkViewed ? 0 : 1; // Inversion logique par rapport à l'ancien code? 
            // Ancien code: vu: item.homeworkViewed ? 0 : 1 (si vu=true -> 0, si vu=false -> 1)
            // isNew = !homeworkViewed.

            files = rawFiles.map((f: any) => {
                const url = this.buildAbsoluteUrl(f.path || f.fileName);
                return {
                    id: f.id,
                    name: f.title || f.fileName || 'Fichier',
                    path: url,
                    url: url,
                };
            });
        }

        const creationDate = item.creationDate || '';
        const color = item.color || '';
        const atomaticDateShort = (item.atomaticDate || '').split(' ')[0];

        let typeLabel = '';
        let displayDate = creationDate;
        let backgroundClass = '';

        const isDoneInClass =
          creationDate === 'Exercice fait en classe' || color === 'yellow';

        if (isDoneInClass) {
          typeLabel = 'Cours';
          displayDate = atomaticDateShort;
          backgroundClass = 'gradient-green';
        } else if (creationDate.toLowerCase().includes('à faire') || color === 'night') {
          typeLabel = 'Exercice à faire';
        } else if (color === 'red') {
          typeLabel = 'Exercice Spécifique';
        }

        return {
          id: item.id,
          matiere: item.matiere || '—',
          creationDate,
          color,
          vu: item.vu || '0',
          atomaticDate: atomaticDateShort,
          nbrExercices: item.nbrExercices || 0,
          name: item.name,
          totalPage: item.totalPage || 0,
          isNew: item.vu === '1',
          isExpanded: false,
          typeLabel,
          displayDate,
          backgroundClass,
        };
      });

      setTimeout(() => {
        this.cahierList = newList;
        this.isLoading = false;
        this.cdr.detectChanges();
      });
    },
    error: (err) => {
      console.error('❌ Error fetching homework list:', err);
      setTimeout(() => (this.isLoading = false));
    },
  });
}




  expandAndMarkSeen(item: CahierItem) {
    // Close all items except the clicked one
    this.cahierList.forEach((i) => {
      if (i !== item) {
        i.isExpanded = false;
      }
    });
    
    // Toggle the clicked item
    const newState = !item.isExpanded;
    item.isExpanded = newState;
    
    // Force change detection
    this.cdr.markForCheck();
    this.cdr.detectChanges();

    if (item.isExpanded && item.isNew && this.selectedStudent && item.id) {
      // Immediately update UI to mark as seen (optimistic update)
      item.isNew = false;
      item.vu = '0';
      
      const idExercice = item.id;
      const idStudent = this.selectedStudent.idStudent;
      this.notificationService.updateNotificationStatus({
        idType: idExercice,
        idStudent: idStudent,
        nameType: 'Homework'
      })
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (response) => {
            // Update notification counts after marking as seen
            this.updateNotificationCounts();
          },
          error: (err) => {
            console.error('Error updating homework notification:', err);
            // Revert the optimistic update if API call fails
            item.isNew = true;
            item.vu = '1';
          },
        });
    }

    if (item.isExpanded && item.id && !item.detailsLoaded) {
      this.loadHomeworkDetails(item);
    }
  }

  private loadHomeworkDetails(item: CahierItem): void {
    if (!item.id) return;

    item.isDetailsLoading = true;
    const url = `${this.apiBaseUrl}/homeWork/getDetailsHomeWork/${item.id}`;

    this.http
      .get<any>(url)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          item.isDetailsLoading = false;
          this.cdr.markForCheck();
          this.cdr.detectChanges();
        })
      )
      .subscribe({
        next: (res) => {
          const hw = res?.response || {};
          item.detailTitle = hw.name || hw.subname || '';
          item.detailCorrectionDate = this.formatDateForDisplay(
            hw.atomicDate || hw.creationDate
          );
          item.detailDescription = hw.description || '';

          const teacherUser = hw.teacher?.user || {};
          const teacherFirst =
            teacherUser.firstName || teacherUser.FirstName || '';
          const teacherLast =
            teacherUser.lastName || teacherUser.LastName || '';
          const teacherFullName = `${teacherFirst} ${teacherLast}`.trim();
          item.detailTeacherName = teacherFullName || '';

          const rawFiles =
            hw.homeworkfiles?.fileEducanet && Array.isArray(hw.homeworkfiles.fileEducanet)
              ? hw.homeworkfiles.fileEducanet
              : [];

          item.detailFiles = rawFiles.map((f: any, index: number) => {
            const fileUrl = this.buildAbsoluteUrl(f.path || f.fileName);
            return {
              id: f.id ?? index,
              name: f.title || f.fileName || 'Fichier',
              path: fileUrl,
              url: fileUrl,
            };
          });

          item.detailsLoaded = true;
        },
        error: (err) => {
          console.error('Error fetching homework details:', err);
        },
      });
  }

  private updateNotificationCounts(): void {
    // Get userData from localStorage to access iduser
    const userData = JSON.parse(localStorage.getItem('userData') || '{}');
    if (userData.iduser) {
      this.notificationService.getNotificationCounts(userData.iduser)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          error: (err) => console.error('Error updating notification counts:', err)
        });
    }
  }

  get totalPages(): number {
    if (this.totalPagesFromApi !== undefined) {
      return this.totalPagesFromApi;
    }
    return Math.ceil(this.totalItems / this.itemsPerPage);
  }

  get paginatedCahierList(): CahierItem[] {
    return this.cahierList;
  }

  goToPage(page: number) {
    if (page >= 1) {
      // Allow navigation to any page >= 1, totalPages will be recalculated after loading
      this.currentPage = page;
      if (this.selectedStudent) {
        this.loadCahier(this.selectedStudent, page);
      }
    }
  }

  previousPage() {
    if (this.currentPage > 1) this.goToPage(this.currentPage - 1);
  }
  nextPage() {
    if (this.currentPage < this.totalPages) {
      this.goToPage(this.currentPage + 1);
    }
  }

  getVisiblePages(): (number | string)[] {
    const totalPages = this.totalPages;
    const currentPage = this.currentPage;
    const visiblePages: (number | string)[] = [];

    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) visiblePages.push(i);
    } else {
      visiblePages.push(1);
      if (currentPage <= 4) {
        for (let i = 2; i <= Math.min(5, totalPages); i++) visiblePages.push(i);
        if (totalPages > 5) {
          visiblePages.push('...');
          visiblePages.push(totalPages);
        }
      } else if (currentPage >= totalPages - 3) {
        visiblePages.push('...');
        for (let i = Math.max(totalPages - 4, 2); i <= totalPages; i++) visiblePages.push(i);
      } else {
        visiblePages.push('...');
        for (let i = currentPage - 1; i <= currentPage + 1; i++)
          visiblePages.push(i);
        visiblePages.push('...');
        visiblePages.push(totalPages);
      }
    }
    return visiblePages;
  }



    // Track by function for better performance
    trackByItemId(index: number, item: CahierItem): any {
      return `${index}-${item.id}-${item.matiere}`; // Unique composite key
    }
    

    downloadHomeworkFile(file: CahierFile | undefined): void {
      if (!file || !file.path) return;
      const url = this.buildAbsoluteUrl(file.path);
      if (typeof window !== 'undefined') {
        window.open(url, '_blank');
      }
    }
}


